import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Loading, Notice, StatusPill } from '../components/ui';
import { FEATURE, SMITH_OUTCOME, flagEmoji, formatDateTime, formatMs, formatTokens, formatUsd, smithRunLabel } from '../lib/format';
import type { SmithRun, SmithTraceDetail } from '../types';
import CompanyLogo from '../components/CompanyLogo';

type Pane = 'outputs' | 'inputs' | 'metadata';

export default function SmithTrace() {
  const { id } = useParams();
  const { data, loading, error } = useApi<SmithTraceDetail>(id ? `/support/smith/traces/${id}` : null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pane, setPane] = useState<Pane>('outputs');

  const selected = useMemo(() => {
    if (!data) return null;
    return data.runs.find((r) => r.id === selectedId) || data.root;
  }, [data, selectedId]);

  if (loading && !data) return <Loading label="Opening trace…" />;
  if (error && !data) return <Notice tone="rose">{error}</Notice>;
  if (!data) return null;

  const { root, runs, remediations } = data;
  const outcome = SMITH_OUTCOME[root.outcome] || SMITH_OUTCOME.NEUTRAL;
  const maxMs = Math.max(...runs.map((r) => r.latencyMs), 1);
  const outputRemediations = remediationsFromOutputs(root);
  const items = remediations.some((r) => !r.changeable) ? remediations : outputRemediations.length ? outputRemediations : remediations;
  const locked = items.filter((r) => !r.changeable);
  const fixable = items.filter((r) => r.changeable);

  return (
    <div className="smith-page smith-trace" data-testid="smith-trace">
      <Link to="/smith" className="text-xs font-bold text-[#d6006e] hover:underline">
        ← Smith
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-6 border-b border-[#f3d0df] pb-6">
        <div className="flex min-w-0 items-start gap-4">
          {root.company && (
            <CompanyLogo companyId={root.company.id} name={root.company.name} hasLogo={root.company.hasLogo} className="h-14 w-14 rounded-xl" />
          )}
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#d6006e]">
              {root.exampleKey === 'good-rating' ? 'Example · good rating' : root.exampleKey === 'unable-to-fix' ? 'Example · unable to fix' : 'Production trace'}
            </p>
            <h1 className="mt-1 font-serif text-3xl font-semibold tracking-tight text-[#2b0018]">
              {root.company
                ? `${flagEmoji(root.company.countryCode)} ${root.company.name}`
                : (root.inputs as { company?: string } | null)?.company || smithRunLabel(root.name)}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#7a1f4a]">{summaryOf(root)}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusPill label={outcome.label} tone={outcome.tone} />
              <StatusPill label={smithRunLabel(root.name)} tone="pink" />
              {root.feature && <StatusPill label={FEATURE[root.feature] || root.feature} tone="slate" />}
              {root.model && <span className="text-[11px] text-[#c084a0]">{root.model}</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="font-serif text-5xl font-semibold leading-none text-[#2b0018]">{root.grade || '—'}</p>
          <p className="mt-1 text-sm font-bold tabular-nums text-[#9d0050]">{root.score != null ? `${root.score} / 100` : 'No score'}</p>
          <p className="mt-2 text-xs text-[#c084a0]">
            {formatMs(root.latencyMs)} · {formatTokens(root.totalTokens)} · {formatUsd(root.costUsd)}
          </p>
          <p className="text-[11px] text-[#c084a0]">{formatDateTime(root.startTime)}</p>
        </div>
      </header>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <section>
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-[#9d0050]">Run waterfall</p>
          <ol className="space-y-1" data-testid="smith-waterfall">
            {runs.map((run) => (
              <li key={run.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(run.id)}
                  className={`smith-span w-full text-left ${selected?.id === run.id ? 'is-active' : ''}`}
                  style={{ paddingLeft: run.parentRunId ? 28 : 12 }}
                  data-testid={`smith-span-${run.name}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-[#2b0018]">
                        {run.parentRunId ? '↳ ' : ''}
                        {smithRunLabel(run.name)}
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{run.runType}</span>
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-[#b0708f]">
                        {run.status === 'error' ? run.error : spanHint(run)}
                      </p>
                    </div>
                    <span className="shrink-0 text-[11px] font-semibold tabular-nums text-[#6b0030]">{formatMs(run.latencyMs)}</span>
                  </div>
                  <span className="smith-latency" style={{ width: `${Math.max(6, (run.latencyMs / maxMs) * 100)}%` }} />
                </button>
              </li>
            ))}
          </ol>

          {(locked.length > 0 || fixable.length > 0) && (
            <div className="mt-8" data-testid="smith-remediation">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-[#9d0050]">Remediation</p>
              <div className="space-y-2">
                {items.map((item, i) => (
                  <article
                    key={'id' in item && item.id ? item.id : `out-${i}`}
                    className={`rounded-xl border px-4 py-3 ${item.changeable ? 'border-[#f3d0df] bg-white' : 'border-[#fecdd3] bg-[#fff1f2]'}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-bold text-[#2b0018]">{item.title}</p>
                      {item.changeable ? (
                        <StatusPill label="Fixable" tone="green" />
                      ) : (
                        <StatusPill label={`Unable to fix${item.immutableKind ? ` · ${item.immutableKind}` : ''}`} tone="rose" />
                      )}
                      {'severity' in item && item.severity && <StatusPill label={item.severity} tone="amber" />}
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-[#7a1f4a]">{item.detail}</p>
                    {'exactChange' in item && item.changeable && item.exactChange && (
                      <p className="mt-1 text-[11px] text-[#9d0050]">Exact change: {item.exactChange}</p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>

        <aside className="smith-inspector" data-testid="smith-inspector">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#f9c0d8]">Inspector</p>
          <h2 className="mt-2 font-serif text-xl text-white">{selected ? smithRunLabel(selected.name) : 'Select a span'}</h2>
          {selected && (
            <>
              <p className="mt-1 text-xs text-[#f9c0d8]">
                {selected.runType} · {selected.status} · {formatMs(selected.latencyMs)}
                {selected.model ? ` · ${selected.model}` : ''}
              </p>
              <div className="mt-4 flex gap-1">
                {(['outputs', 'inputs', 'metadata'] as Pane[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPane(p)}
                    className={`rounded-full px-3 py-1 text-[11px] font-bold capitalize ${pane === p ? 'bg-white text-[#2b0018]' : 'text-[#f9c0d8] hover:bg-white/10'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="smith-pre mt-4">
                {pane === 'outputs' && <JsonBlock value={selected.outputs} fallback={selected.error} />}
                {pane === 'inputs' && <JsonBlock value={selected.inputs} />}
                {pane === 'metadata' && (
                  <JsonBlock
                    value={{
                      id: selected.id,
                      traceId: selected.traceId,
                      feature: selected.feature,
                      tags: selected.tags,
                      tokens: { prompt: selected.promptTokens, output: selected.outputTokens, total: selected.totalTokens },
                      costUsd: selected.costUsd,
                      extra: selected.extra,
                    }}
                  />
                )}
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function summaryOf(run: SmithRun): string {
  const outputs = run.outputs as { summary?: string } | null;
  return outputs?.summary || run.error || SMITH_OUTCOME[run.outcome]?.hint || '';
}

function spanHint(run: SmithRun): string {
  const outputs = run.outputs as { verdict?: string; summary?: string; grade?: string; score?: number } | null;
  if (outputs?.grade && outputs.score != null) return `${outputs.grade} · ${outputs.score}/100`;
  if (outputs?.verdict) return `${outputs.verdict}${outputs.summary ? ` · ${outputs.summary}` : ''}`;
  if (outputs?.summary) return outputs.summary;
  return run.model || run.runType;
}

function remediationsFromOutputs(root: SmithRun): { title: string; detail: string; changeable: boolean; immutableKind: string | null; severity?: string; id?: string; exactChange?: string | null }[] {
  const outputs = root.outputs as { remediations?: { title: string; detail?: string; changeable?: boolean; immutableKind?: string | null; severity?: string }[] } | null;
  if (!Array.isArray(outputs?.remediations)) return [];
  return outputs.remediations.map((r) => ({
    title: r.title,
    detail: r.detail || '',
    changeable: r.changeable !== false,
    immutableKind: r.immutableKind || null,
    severity: r.severity,
  }));
}

function JsonBlock({ value, fallback }: { value: unknown; fallback?: string | null }) {
  if (value == null || value === '') {
    return <p className="text-sm text-[#f9c0d8]">{fallback || 'No payload captured for this span.'}</p>;
  }
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return <pre data-testid="smith-json">{text}</pre>;
}
